import { type ChangeEvent, type Dispatch, memo, type SetStateAction } from "react";
import "Components/ConfigLayout/Event/ActionSettings/AlarmOut/AlarmOut.less";
import {
	Checkbox, Icon, IconSize, IconWeight, Input, type SelectOption, Popover, Select
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { ActionState } from "Interfaces";
import { Link } from "react-router-dom";
import { AlarmOutOperation, Operation } from "Data/Objects/Event";

const operationOptions: SelectOption[] = [
	{
		label: AlarmOutOperation.ALARM_OUT,
		value: AlarmOutOperation.ALARM_OUT
	},
	{
		label: AlarmOutOperation.ON,
		value: AlarmOutOperation.ON
	},
	{
		label: AlarmOutOperation.OFF,
		value: AlarmOutOperation.OFF
	}
];

const typeOptions: SelectOption[] = [
	{
		label: Operation.NORMALLY_OPEN,
		value: Operation.NORMALLY_OPEN
	},
	{
		label: Operation.NORMALLY_CLOSED,
		value: Operation.NORMALLY_CLOSED
	}
];

const durationOptions: SelectOption[] = [ 0, 5, 10, 15, 30, 60, 120, 180, 300 ].map(value => ({
	label: value === 0 ? "Transparent" : `${ value } Sec`,
	value
}));

type AlarmOutProps = {
	localActionState: [ ActionState, Dispatch<SetStateAction<ActionState>> ];
};

function AlarmOut(props: AlarmOutProps) {
	const { localActionState: [ localActionSettings, setLocalActionSettings ] } = props;
	const link = (
		<div>
			Go to <Link to="/setup/system/schedule">Schedule Preset</Link> to create a new preset.
		</div>
	);

	const handleNameUpdate = (name: string) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.alarmOut.name = name;
		}));
	};

	const handleScheduleChange = (scheduleName: string) => (event: ChangeEvent<HTMLInputElement>) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.alarmOut.schedule[ scheduleName ] = event.currentTarget.checked;
		}));
	};

	const handleOperationSelect = (operation: string | null) => {
		if (!operation) {
			return;
		}

		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.alarmOut.operation = operation as AlarmOutOperation;
		}));
	};

	const handleTypeSelect = (type: string | null) => {
		if (!type) {
			return;
		}

		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.alarmOut.type = type as Operation;
		}));
	};

	const handleDurationSelect = (duration: string | null) => {
		if (!duration) {
			return;
		}

		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.alarmOut.duration = +duration;
		}));
	};

	const renderSchedule = (schedule: [ string, boolean ]) => {
		const [ scheduleName, checked ] = schedule;

		return (
			<Checkbox checked={ checked } key={ scheduleName } onChange={ handleScheduleChange(scheduleName) }>
				<span>{ scheduleName }</span>
			</Checkbox>
		);
	};

	return (
		<div className="alarm-out">
			<div className="column">
				<div className="page-title">
					<h4>Alarm 1</h4>
				</div>
				<div className="form-row">
					<span>Name</span>
					<Input value={ localActionSettings.alarmOut.name } onUpdate={ handleNameUpdate } />
				</div>
				<div className="form-row">
					<span>Operation</span>
					<Select
						allowClear={ false }
						onSelect={ handleOperationSelect }
						options={ operationOptions }
						value={ localActionSettings.alarmOut.operation }
					/>
				</div>
				<div className="form-row">
					<span>Type</span>
					<Select
						allowClear={ false }
						onSelect={ handleTypeSelect }
						options={ typeOptions }
						value={ localActionSettings.alarmOut.type }
					/>
				</div>
				<div className="form-row">
					<span>Duration</span>
					<Select
						allowClear={ false }
						onSelect={ handleDurationSelect }
						options={ durationOptions }
						value={ localActionSettings.alarmOut.duration }
					/>
				</div>
			</div>
			<div className="column">
				<div className="schedule-title">
					<h4>Schedule</h4>
					<Popover content={ link }>
						<Icon name="circle-info" iconWeight={ IconWeight.REGULAR } size={ IconSize.SMALLER } />
					</Popover>
				</div>
				<div className="schedules">
					{ Object.entries(localActionSettings.alarmOut.schedule).map(renderSchedule) }
				</div>
			</div>
		</div>
	);
}

export default memo(AlarmOut);
